import { getAdobeEnvironmentId } from 'src/utils/tenant';

export const ADOBE_BASE_URL = 'https://cpcontents.adobe.com/public/newlearner/newlearner_2fa1a0bd.html';

export interface AdobeLearningObject {
  id: string;
  title: string;
  slug: string;
  description: string;
  type: 'course' | 'learningPath' | 'certification' | 'lesson';
  url: string;
  duration: number;
}

export interface AdobeLearningObjectsResponse {
  data: AdobeAPILearningObject[];
  links?: {
    next?: string;
  };
  meta?: {
    count?: number;
    formalCount?: number;
  };
}

interface AdobeAPILearningObject {
  id: string;
  attributes?: {
    localizedMetadata?: Array<{
      name?: string;
      description?: string;
    }>;
    name?: string;
    description?: string;
    loType?: string;
    duration?: number;
  };
}

export interface AdobeLearningPath {
  type: 'learningObject';
  id: string;
  attributes: {
    localizedMetadata: {
      description: string;
      locale: string;
      name: string;
      overview: string;
      richTextOverview: string;
    }[];
    tags: string[];
  };
  relationships: unknown;
}

export interface AdobeLearningPathsResponse {
  data: AdobeLearningPath[];
}

export const fetchAdobeLearningObjects = async (
  catalogId?: string | string[],
  limit: number = 10,
  cursor?: string,
  filters?: { [key: string]: string[] },
): Promise<AdobeLearningObjectsResponse> => {
  const params = new URLSearchParams();

  params.append('page[limit]', limit.toString());
  if (cursor) {
    params.append('page[cursor]', cursor);
  }

  if (catalogId) {
    const catalogIds = Array.isArray(catalogId) ? catalogId : [catalogId];

    if (catalogIds.length > 0) {
      params.append('filter.catalogIds', catalogIds.join(','));
    }
  }

  const defaultLoTypes = ['course', 'learningProgram', 'certification'];
  // Only use default types if no filters object provided OR if loTypes is explicitly undefined
  // If loTypes is an empty array [], respect that (don't default)
  const loTypes = filters && 'loTypes' in filters ? filters.loTypes : defaultLoTypes;

  if (loTypes.length > 0) {
    params.append('filter.loTypes', loTypes.join(','));
  }

  if (loTypes.includes('learningProgram')) {
    params.append('filter.ignoreEnhancedLP', 'false');
  }

  if (filters) {
    Object.keys(filters).forEach((key) => {
      if (key === 'loTypes' || key === 'collectionProduct') {
        return;
      }

      if (!filters[key] || !Array.isArray(filters[key])) {
        return;
      }

      const validValues = filters[key].filter((value) => value);

      if (validValues.length === 0) {
        return;
      }

      params.append(`filter.${key}`, validValues.join(','));
    });
  }

  const url = `https://learningmanager.adobe.com/primeapi/v2/learningObjects?${params.toString()}`;

  const token = getAdobeToken();

  const headers = {
    Accept: 'application/vnd.api+json',
    Authorization: `oauth ${token}`,
  };

  const response = await fetch(url, { method: 'GET', headers });

  if (!response.ok) {
    throw new Error(`Error: ${response.status} - ${response.statusText}`);
  }

  const jsonResponse = await response.json();

  return jsonResponse;
};

export const fetchAdobeCatalogs = async (): Promise<Array<{ id: string; name: string }>> => {
  const url = 'https://learningmanager.adobe.com/primeapi/v2/catalogs';

  const token = getAdobeToken();

  const headers = {
    Accept: 'application/vnd.api+json',
    Authorization: `oauth ${token}`,
  };

  const response = await fetch(url, { method: 'GET', headers });

  if (!response.ok) {
    throw new Error(`Error fetching catalogs: ${response.status} - ${response.statusText}`);
  }

  const jsonResponse = await response.json();

  interface CatalogItem {
    id: string;
    attributes?: {
      localizedMetadata?: Array<{ name?: string }>;
      name?: string;
    };
  }

  return (
    jsonResponse.data?.map((catalog: CatalogItem) => ({
      id: catalog.id,
      name: catalog.attributes?.localizedMetadata?.[0]?.name || catalog.attributes?.name || 'Unnamed Catalog',
    })) || []
  );
};

export const transformAdobeLearningObjects = async (
  response: AdobeLearningObjectsResponse | AdobeSearchResponse,
): Promise<{
  resources: AdobeLearningObject[];
  totalCount: number;
  hasNext: boolean;
}> => {
  const accountId = 'mock-account-id'; // TODO: get account id
  const resources: AdobeLearningObject[] =
    response.data?.map((item: AdobeAPILearningObject) => {
      // Use modelSubType for search responses, fallback to loType for regular responses
      const typeField = (item.attributes as { modelSubType?: string })?.modelSubType || item.attributes?.loType;

      return {
        id: item.id,
        title: item.attributes?.localizedMetadata?.[0]?.name || item.attributes?.name || 'Untitled',
        slug: cleanAdobeId(item.id),
        description: item.attributes?.localizedMetadata?.[0]?.description || item.attributes?.description || '',
        type: mapAdobeTypeToLocalType(typeField),
        url: `https://learningmanager.adobe.com/app/learner?accountId=${accountId}#/${mapAdobeTypeToUrlPath(
          typeField,
        )}/${cleanAdobeId(item.id)}`,

        duration: item.attributes?.duration || 0,
      };
    }) || [];

  // Handle both response types - search has formalCount, learningObjects has count
  const totalCount = response.meta?.formalCount || response.meta?.count || 0;

  return {
    resources,
    totalCount,
    hasNext: !!response.links?.next,
  };
};

interface AdobeAPIDataItem {
  id?: string;
  attributes?: {
    names?: string[];
  };
}

interface AdobeAPIResponse {
  data?: AdobeAPIDataItem;
}

export interface AdobeTaxonomyData {
  catalog: Array<{ value: string; label: string }>;
  loType: Array<{ value: string; label: string }>;
  loFormat: Array<{ value: string; label: string }>;
  duration: Array<{ value: string; label: string }>;
  skills: Array<{ value: string; label: string }> | undefined;
  skillLevels: Array<{ value: string; label: string }>;
  tags: Array<{ value: string; label: string }> | undefined;
  learnerState: Array<{ value: string; label: string }>;
}

export const fetchAdobeTaxonomyData = async (): Promise<AdobeTaxonomyData> => {
  const headers = {
    Accept: 'application/vnd.api+json',
    Authorization: `oauth ${getAdobeToken()}`,
  };

  const taxonomyData: AdobeTaxonomyData = {
    catalog: [], // Will be populated from API
    loType: [
      { value: 'course', label: 'Course' },
      { value: 'learningProgram', label: 'Learning Path' },
      { value: 'certification', label: 'Certification' },
    ],
    loFormat: [
      { value: 'blended', label: 'Blended' },
      { value: 'selfPaced', label: 'Self Paced' },
      { value: 'virtualClassroom', label: 'Virtual Classroom' },
      { value: 'classroom', label: 'Classroom' },
      { value: 'activity', label: 'Activity' },
    ],
    duration: [
      { value: '0-1800', label: '30 mins or less' },
      { value: '1801-7200', label: '30 mins to 2 hours' },
      { value: '7201-3600000', label: '2 hours+' },
    ],
    skills: [],
    skillLevels: [
      { value: '1', label: 'Beginner' },
      { value: '2', label: 'Intermediate' },
      { value: '3', label: 'Advanced' },
    ],
    tags: [],
    learnerState: [
      { value: 'enrolled', label: 'Yet To Start' },
      { value: 'started', label: 'In Progress' },
      { value: 'completed', label: 'Completed' },
      { value: 'notenrolled', label: 'Not Enrolled' },
    ],
  };

  try {
    const catalogsResponse = await fetchAdobeCatalogs();

    taxonomyData.catalog = catalogsResponse.map((catalog) => ({
      value: catalog.id,
      label: catalog.name,
    }));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to fetch Adobe catalogs:', error);
  }

  const dataTypesToFetch = ['skillName', 'tagName'];

  try {
    const fetchPromises = dataTypesToFetch.map(async (dataType) => {
      const url = `https://learningmanager.adobe.com/primeapi/v2/data?filter.${dataType}=true`;
      const response = await fetch(url, { method: 'GET', headers });

      if (!response.ok) {
        throw new Error(`Error fetching ${dataType}: ${response.status} - ${response.statusText}`);
      }

      const responseData: AdobeAPIResponse = await response.json();

      return { dataType, data: responseData };
    });

    const results = await Promise.all(fetchPromises);

    results.forEach(({ dataType, data }) => {
      const items = data.data;

      switch (dataType) {
        case 'skillName':
          taxonomyData.skills = items?.attributes?.names?.map((name) => ({
            value: name,
            label: name,
          }));
          break;
        case 'tagName':
          taxonomyData.tags = items?.attributes?.names?.map((name) => ({
            value: name,
            label: name,
          }));
          break;
        default:
          break;
      }
    });

    return taxonomyData;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch Adobe taxonomy data:', error);

    return taxonomyData;
  }
};

const adobeTypeMapping = {
  course: {
    localType: 'course',
    urlPath: 'course',
  },
  learningprogram: {
    localType: 'learningPath',
    urlPath: 'learningProgram',
  },
  certification: {
    localType: 'certification',
    urlPath: 'certification',
  },
};

const mapAdobeTypeToLocalType = (adobeType?: string): AdobeLearningObject['type'] => {
  const mapping = adobeTypeMapping[adobeType?.toLowerCase() as keyof typeof adobeTypeMapping];

  return (mapping?.localType as AdobeLearningObject['type']) || 'course';
};

const mapAdobeTypeToUrlPath = (adobeType?: string): string => {
  const mapping = adobeTypeMapping[adobeType?.toLowerCase() as keyof typeof adobeTypeMapping];

  return mapping?.urlPath || 'course';
};

export const createAdobeLabelToValueMap = (
  taxonomyData: AdobeTaxonomyData,
): { [key: string]: { [label: string]: string } } => {
  const mapping: { [key: string]: { [label: string]: string } } = {};

  mapping.loType = {};
  taxonomyData.loType.forEach((item) => {
    mapping.loType[item.label] = item.value;
  });

  mapping.loFormat = {};
  taxonomyData.loFormat.forEach((item) => {
    mapping.loFormat[item.label] = item.value;
  });

  mapping.duration = {};
  taxonomyData.duration.forEach((item) => {
    mapping.duration[item.label] = item.value;
  });

  mapping.skills = {};
  taxonomyData.skills?.forEach((item) => {
    mapping.skills[item.label] = item.value;
  });

  mapping.skillLevels = {};
  taxonomyData.skillLevels.forEach((item) => {
    mapping.skillLevels[item.label] = item.value;
  });

  mapping.tags = {};
  taxonomyData.tags?.forEach((item) => {
    mapping.tags[item.label] = item.value;
  });

  mapping.learnerState = {};
  taxonomyData.learnerState.forEach((item) => {
    mapping.learnerState[item.label] = item.value;
  });

  mapping.catalog = {};
  taxonomyData.catalog.forEach((item) => {
    mapping.catalog[item.label] = item.value;
  });

  return mapping;
};

export interface AdobeSearchResponse {
  data?: AdobeAPILearningObject[];
  links?: {
    next?: string;
  };
  meta?: {
    count?: number;
    formalCount?: number;
  };
}

export const searchAdobeLearningObjects = async (
  query: string,
  catalogIds?: string[],
  filters?: { [key: string]: string[] },
  limit: number = 10,
  cursor?: string,
): Promise<AdobeSearchResponse | undefined> => {
  const params = new URLSearchParams();

  params.append('query', query);

  params.append('page[limit]', limit.toString());
  if (cursor) {
    params.append('page[cursor]', cursor);
  }

  const defaultLoTypes = ['course', 'learningProgram', 'certification'];

  const hasNonCatalogFilters = filters && Object.keys(filters).some((key) => key !== 'catalog' && key !== 'search');

  if (!hasNonCatalogFilters) {
    if (catalogIds && catalogIds.length > 0) {
      params.append('filter.catalogIds', catalogIds.join(','));
    }
    params.append('filter.loTypes', defaultLoTypes.join(','));

    if (defaultLoTypes.includes('learningProgram')) {
      params.append('filter.ignoreEnhancedLP', 'false');
    }
  } else {
    if (catalogIds && catalogIds.length > 0) {
      params.append('filter.catalogIds', catalogIds.join(','));
    }

    let loTypes = defaultLoTypes;

    if (filters.loTypes !== undefined) {
      loTypes = filters.loTypes;
    } else if (filters.loType && filters.loType.length > 0) {
      loTypes = filters.loType;
    } else if (filters.type && filters.type.length > 0) {
      loTypes = filters.type;
    }

    if (loTypes.length > 0) {
      params.append('filter.loTypes', loTypes.join(','));
    }

    if (filters) {
      Object.keys(filters).forEach((key) => {
        if (
          key === 'loTypes' ||
          key === 'loType' ||
          key === 'type' ||
          key === 'catalog' ||
          key === 'search' ||
          key === 'collectionProduct' ||
          key === 'lessonProduct'
        ) {
          return;
        }

        if (!filters[key] || !Array.isArray(filters[key])) {
          return;
        }

        const validValues = filters[key].filter((value) => value);

        if (validValues.length === 0) {
          return;
        }

        params.append(`filter.${key}`, validValues.join(','));
      });
    }

    if (params.get('filter.loTypes')?.includes('learningProgram')) {
      params.append('filter.ignoreEnhancedLP', 'false');
    }
  }

  const url = `https://learningmanager.adobe.com/primeapi/v2/search?${params.toString()}`;

  const token = getAdobeToken();

  const headers = {
    Accept: 'application/vnd.api+json',
    Authorization: `oauth ${token}`,
  };

  const response = await fetch(url, { method: 'GET', headers });

  if (!response.ok) {
    throw new Error(`Error searching Adobe Learning Objects: ${response.status} - ${response.statusText}`);
  }

  const jsonResponse = await response.json();

  return jsonResponse;
};

export const ADOBE_FILTER_MAPPING = {
  loType: 'loTypes',
  loFormat: 'loFormat',
  duration: 'duration.range',
  skills: 'skillName',
  skillLevels: 'skill.level',
  tags: 'tagName',
  learnerState: 'learnerState',
  catalog: 'catalogIds',
} as const;

export const getDefaultAdobeLabelToValueMap = (): { [key: string]: { [label: string]: string } } => ({
  loType: {
    Course: 'course',
    'Learning Path': 'learningProgram',
    Certification: 'certification',
  },
  loFormat: {
    Blended: 'Blended',
    'Self Paced': 'Self Paced',
    'Virtual Classroom': 'Virtual Classroom',
    Classroom: 'Classroom',
    Activity: 'Activity',
  },
  duration: {
    '30 mins or less': '0-1800',
    '30 mins to 2 hours': '1801-7200',
    '2 hours': '7201-3600000',
    '2 hours+': '7201-3600000',
  },
  skillLevels: {
    Beginner: '1',
    Intermediate: '2',
    Advanced: '3',
  },
  learnerState: {
    'Yet To Start': 'enrolled',
    'In Progress': 'started',
    Completed: 'completed',
    'Not Enrolled': 'notenrolled',
  },
  skills: {},
  tags: {},
  catalog: {
    // Will be populated from API response
  },
});

export const cleanAdobeId = (adobeId: string): string => {
  if (adobeId.includes(':')) {
    return adobeId.split(':')[1];
  }

  return adobeId;
};

export const fetchAdobeLearningPaths = async (catalogIds: string[] = []): Promise<AdobeLearningPath[]> => {
  const filterQuery = `filter.ignoreEnhancedLP=false&filter.loTypes=learningProgram&filter.catalogIds=${catalogIds.join(
    ',',
  )}`;
  const url = `https://learningmanager.adobe.com/primeapi/v2/learningObjects?${filterQuery}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/vnd.api+json',
        Authorization: `oauth ${getAdobeToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} - ${response.statusText}`);
    }

    const { data } = (await response.json()) as AdobeLearningPathsResponse;

    return data;
  } catch (error) {
    /* eslint-disable-next-line no-console */
    console.error('Error fetching courses:', error);

    return [];
  }
};

// TODO: Remove this, a relic of old Adobe POC. This is just a temporary stub while other logic depending on this function
// is not yet cleaned up.
// Using a function declaration instead of const to make it mockable in tests
export function getAdobeToken(): string {
  return 'mock-token';
}

/**
 * Builds an Adobe URL with the account ID and optional hash route.
 * @param hashRoute - The hash route path (e.g., '/catalog/index'). If not provided, returns base URL.
 * @returns The complete Adobe URL with accountId and hash route
 */
export const buildAdobeUrl = (hashRoute?: string): string => {
  const accountId = getAdobeEnvironmentId();

  if (!accountId) {
    return ADOBE_BASE_URL;
  }

  const baseUrl = `${ADOBE_BASE_URL}?accountId=${accountId}`;

  if (!hashRoute) {
    return baseUrl;
  }

  // Ensure hash route starts with '#'
  const formattedRoute = hashRoute.startsWith('#') ? hashRoute : `#${hashRoute}`;

  return `${baseUrl}${formattedRoute}`;
};

/**
 * Gets the Adobe course catalog URL with the catalog index route.
 * @returns The Adobe course catalog URL (e.g., ...?accountId=136474#/catalog/index)
 */
export const getAdobeCourseCatalogUrl = (): string => {
  return buildAdobeUrl('/catalog/index');
};
